import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import {
  CaseBreakdown,
  CaseGallery,
  CaseHero,
  RelatedCases,
  caseStudyArticleSchema,
} from "@/components/case-study/CaseStudySections";
import { JsonLd } from "@/components/JsonLd";
import { CASES } from "@/content/cases";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InlineLeadForm } from "@/components/InlineLeadForm";
import { ProofLink } from "@/components/ProofLink";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }) => {
    const c = CASES.find((x) => x.slug === params.slug);
    if (!c) throw notFound();
    return { c };
  },
  head: ({ loaderData }) => {
    const c = loaderData?.c;
    if (!c) return { meta: [{ title: "Case Study Not Found" }] };
    return buildSeo({
      title: `${c.headline} — SEO Case Study | Freelancer Tamal`,
      // Summaries are shared evidence notes, so lead with the case-specific context.
      description: `${c.headline} (${c.industry}, ${c.region}). ${c.summary}`,
      path: `/case-studies/${c.slug}`,
      alternates: { bn: `/bn/case-studies/${c.slug}` },
      type: "article",
      keywords: `${c.industry}, ${c.region}, SEO case study, ${c.client}`,
      ogEyebrow: `Case Study · ${c.region}`,
      ogTitle: c.headline,
    });
  },
  component: Detail,
  notFoundComponent: () => (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-4xl">Case study not found</h1>
      <Link to="/case-studies" className="mt-6 inline-flex text-primary">← All case studies</Link>
    </div>
  ),
});

function Detail() {
  const { c } = Route.useLoaderData();
  const related = CASES.filter((x) => x.slug !== c.slug).slice(0, 3);
  return (
    <>
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: c.client, path: `/case-studies/${c.slug}` },
        ]}
      />
      <JsonLd data={caseStudyArticleSchema(c, "en")} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: c.client, path: `/case-studies/${c.slug}` },
        ])}
      />

      <CaseHero c={c} lang="en" />
      <CaseGallery c={c} />
      <CaseBreakdown c={c} lang="en" footer={<ProofLink subjectPath={`/case-studies/${c.slug}`} />} />
      <RelatedCases related={related} lang="en" />

      <Section className="!pt-0">
        <InlineLeadForm
          title="Plan your SEO priorities"
          description={`I help ${c.industry.toLowerCase()} teams in ${c.region} and worldwide plan relevant SEO work. Send a quick brief — I'll reply within one business day.`}
          source={`case-study:${c.slug}`}
        />
      </Section>
    </>
  );
}
